import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TextField, Button, CircularProgress, Typography, Paper, Collapse, Box } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

const BACKEND = 'https://lobster-app-sd3s8.ondigitalocean.app';

const ChatInterface = ({ token, reportId, sessionId, disabled, onChatHistoryChange, handleError }) => {
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [expanded, setExpanded] = useState({}); // Track expanded state for each chat entry

  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(`${BACKEND}/api/chat/history/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.status === 200) {
          throw response.data;
        }
        setChatHistory(response.data.chatHistory || []);
        onChatHistoryChange(response.data.chatHistory || []);
        // Initialize all chats as collapsed
        setExpanded(
          (response.data.chatHistory || []).reduce((acc, _, index) => {
            acc[index] = false;
            return acc;
          }, {})
        );
      } catch (err) {
        handleError(err, () => fetchChatHistory());
      }
    };
    if (reportId && token) {
      fetchChatHistory();
    }
  }, [reportId, token, handleError, onChatHistoryChange]);

  useEffect(() => {
    if (jobId) {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${BACKEND}/api/job/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!response.status === 200) {
            throw response.data;
          }
          const { status, result, error } = response.data;
          if (status === 'completed') {
            const newEntry = { question, answer: result.answer };
            const newChatHistory = [...chatHistory, newEntry];
            setChatHistory(newChatHistory);
            onChatHistoryChange(newChatHistory);
            setExpanded(prev => ({
              ...prev,
              [newChatHistory.length - 1]: true // Expand the new chat entry
            }));
            setQuestion('');
            setLoading(false);
            setJobId(null);
          } else if (status === 'failed') {
            handleError({ message: error || 'Failed to process your question.' });
            setLoading(false);
            setJobId(null);
          }
        } catch (err) {
          handleError(err, () => setJobId(jobId));
          setLoading(false);
          setJobId(null);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [jobId, chatHistory, question, token, handleError, onChatHistoryChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || disabled) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND}/api/chat`,
        { reportId, sessionId, question },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status !== 200) {
        throw new Error('Failed to submit chat question');
      }
      setJobId(response.data.jobId);
    } catch (err) {
      console.error('ChatInterface error:', err);
      handleError(err, () => handleSubmit(e)); // Pass the event to retry
      setLoading(false);
    }
  };

  const toggleExpand = (index) => {
    setExpanded(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="chat-container">
      <Typography variant="h2">Ask Questions About the Sitemaps</Typography>
      <div className="chat-history">
        {chatHistory.map((entry, index) => (
          <div key={index} className="chat-entry">
            <Box
              display="flex"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={() => toggleExpand(index)}
            >
              <Typography sx={{ flexGrow: 1 }}>
                <strong>Q:</strong> {entry.question}
              </Typography>
              {expanded[index] ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={expanded[index]}>
              <Paper elevation={1} className="chat-answer" sx={{ mt: 1 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {entry.answer}
                </ReactMarkdown>
              </Paper>
            </Collapse>
          </div>
        ))}
      </div>
      {loading && (
        <div className="loading-indicator">
          <CircularProgress />
          <Typography>Processing your question...</Typography>
        </div>
      )}
      <form onSubmit={handleSubmit} className="chat-form">
        <TextField
          variant="outlined"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask a question about the sitemaps..."
          disabled={disabled || loading}
          fullWidth
          className="chat-input"
        />
        <Button type="submit" variant="contained" color="primary" disabled={disabled || loading}>
          {loading ? 'Asking...' : 'Ask'}
        </Button>
      </form>
    </div>
  );
};

export default ChatInterface;